import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Check, X, Phone, Moon, Utensils, Users } from "lucide-react";
import { StatusBadge, FilterTabs, fmtDate, fmtStamp } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ["all", "requested", "confirmed", "completed", "cancelled"];

const openWhatsApp = (url) => window.open(url, "_blank", "noopener,noreferrer");

const ActionBtn = ({ children, onClick, tone = "ghost", testId, disabled }) => {
  const tones = {
    primary: "bg-pine text-cream hover:bg-royal border-pine",
    whatsapp: "bg-[#25D366] text-ink hover:bg-[#1ebe5b] border-[#25D366]",
    ghost: "bg-white text-pine border-sand hover:border-pine",
    danger: "bg-white text-red-700 border-red-200 hover:bg-red-50",
  };
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 disabled:opacity-50 ${tones[tone]}`}
    >
      {children}
    </button>
  );
};

function VisitRow({ v, onUpdate }) {
  const [note, setNote] = useState(v.admin_note || "");
  const [busy, setBusy] = useState(false);

  const patch = async (body, successMsg) => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/admin/visits/${v.id}`, body, { withCredentials: true });
      onUpdate(r.data);
      if (successMsg) toast.success(successMsg);
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const confirmAndWhatsApp = async () => {
    const updated = await patch(
      { status: "confirmed", payment_status: "paid" },
      "Marked paid & confirmed — opening WhatsApp"
    );
    if (updated?.whatsapp?.url) openWhatsApp(updated.whatsapp.url);
  };

  const saveNote = () => {
    if (note !== (v.admin_note || "")) patch({ admin_note: note }, "Note saved");
  };

  return (
    <article
      data-testid={`admin-visit-row-${v.id}`}
      className="rounded-3xl border border-sand bg-white p-6 md:p-7 grid lg:grid-cols-12 gap-6"
    >
      <div className="lg:col-span-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">Visit date</p>
        <p className="mt-2 font-serif text-2xl text-pine leading-tight">{fmtDate(v.visit_date)}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <StatusBadge value={v.status} testId={`admin-visit-status-${v.id}`} />
          <StatusBadge value={v.payment_status || "pending"} testId={`admin-visit-payment-${v.id}`} />
        </div>
        <p className="mt-3 text-xs text-sage">Requested {fmtStamp(v.created_at)}</p>
      </div>

      <div className="lg:col-span-4">
        <p className="font-serif text-xl text-coal">{v.name}</p>
        <a
          href={`tel:${v.phone}`}
          className="mt-1 inline-flex items-center gap-2 text-sm text-pine hover:text-royal"
        >
          <Phone className="h-3.5 w-3.5" /> {v.phone}
        </a>
        {v.email && <p className="mt-0.5 text-xs text-sage break-all">{v.email}</p>}
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-sage">
          <span className="inline-flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" /> {v.visitors} guest{v.visitors > 1 ? "s" : ""}
          </span>
          {v.meals && (
            <span className="inline-flex items-center gap-1.5">
              <Utensils className="h-3.5 w-3.5" /> Meals
            </span>
          )}
          {v.night_stay && (
            <span className="inline-flex items-center gap-1.5">
              <Moon className="h-3.5 w-3.5" /> Night stay · ₹{(v.estimated_amount || 3500).toLocaleString("en-IN")}
            </span>
          )}
        </div>
        {v.notes && <p className="mt-3 text-sm text-coal/80 leading-relaxed italic">“{v.notes}”</p>}
      </div>

      <div className="lg:col-span-5 flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          {v.status !== "confirmed" && v.status !== "completed" && v.status !== "cancelled" && (
            <ActionBtn
              tone="primary"
              testId={`admin-visit-confirm-${v.id}`}
              onClick={confirmAndWhatsApp}
              disabled={busy}
            >
              <Check className="h-3.5 w-3.5" /> Mark paid & confirm
            </ActionBtn>
          )}
          {v.status === "confirmed" && (
            <>
              <ActionBtn
                tone="whatsapp"
                testId={`admin-visit-whatsapp-${v.id}`}
                onClick={() => openWhatsApp(v.whatsapp?.url)}
              >
                <MessageCircle className="h-3.5 w-3.5" /> Send WhatsApp
              </ActionBtn>
              <ActionBtn
                testId={`admin-visit-complete-${v.id}`}
                onClick={() => patch({ status: "completed" }, "Visit marked completed")}
                disabled={busy}
              >
                Mark completed
              </ActionBtn>
            </>
          )}
          {v.status !== "cancelled" && v.status !== "completed" && (
            <ActionBtn
              tone="danger"
              testId={`admin-visit-cancel-${v.id}`}
              onClick={() => patch({ status: "cancelled" }, "Visit cancelled")}
              disabled={busy}
            >
              <X className="h-3.5 w-3.5" /> Cancel
            </ActionBtn>
          )}
          {v.status === "cancelled" && (
            <ActionBtn
              testId={`admin-visit-reopen-${v.id}`}
              onClick={() => patch({ status: "requested", payment_status: "pending" }, "Reopened")}
              disabled={busy}
            >
              Reopen
            </ActionBtn>
          )}
        </div>
        <label className="grid gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">
            Farm note (payment ref, arrival time…)
          </span>
          <textarea
            data-testid={`admin-visit-note-${v.id}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={saveNote}
            placeholder="e.g. UPI ₹3,500 received 2 Oct · arriving 8 AM"
            className="w-full rounded-xl bg-parchment border border-sand px-3.5 py-2.5 text-sm text-coal outline-none focus:border-pine min-h-[64px] resize-y"
          />
        </label>
      </div>
    </article>
  );
}

export default function AdminVisits({ visits, setVisits }) {
  const [filter, setFilter] = useState("all");

  const counts = useMemo(() => {
    const c = { all: visits.length };
    FILTERS.slice(1).forEach((f) => (c[f] = visits.filter((v) => v.status === f).length));
    return c;
  }, [visits]);

  const shown = filter === "all" ? visits : visits.filter((v) => v.status === filter);

  const onUpdate = (updated) =>
    setVisits((list) => list.map((v) => (v.id === updated.id ? updated : v)));

  return (
    <div data-testid="admin-visits-panel">
      <FilterTabs options={FILTERS} value={filter} onChange={setFilter} counts={counts} prefix="admin-visits" />
      <div className="mt-6 grid gap-4">
        {shown.length === 0 ? (
          <p data-testid="admin-visits-empty" className="rounded-3xl border border-dashed border-sand bg-white/60 p-10 text-center text-sm text-sage">
            No farm-visit requests here yet.
          </p>
        ) : (
          shown.map((v) => <VisitRow key={v.id} v={v} onUpdate={onUpdate} />)
        )}
      </div>
    </div>
  );
}
